import * as webeid from '@web-eid/web-eid-library'
import { MOCK_CERTIFICATE, MOCK_PRIVATE_KEY } from './mockSigningKey'

const STAMPIT_SERVICE_URL = 'http://localhost:8090'
// Our own bridge for qualified cards that no browser extension reaches. Named for the Slovak cards;
// it also serves RS (see SLOVAK_SIGNER_COUNTRIES). Bulgaria's StampIT is on 8090.
const SK_SIGNER_URL = 'http://localhost:8091'
const PROBE_TIMEOUT_MS = 2000

export type SigningMethod = 'tokensigning' | 'stampit' | 'sk-signer' | 'webeid' | 'software'

export interface SignResult {
  signature: string
  signatureAlgorithm?: string
}

export interface SigningSession {
  method: SigningMethod
  certificate: string
  sign: (dataToSign: string) => Promise<SignResult>
}

interface TokenSigningCertificate {
  hex: string
}

interface TokenSigningSignature {
  hex: string
}

interface TokenSigningHash {
  hex: string
  type: 'SHA-256'
}

interface TokenSigningInstance {
  getCertificate(options: { lang?: string; filter?: string }): Promise<TokenSigningCertificate>
  sign(
    cert: TokenSigningCertificate,
    hash: TokenSigningHash,
    options: { lang?: string; info?: string },
  ): Promise<TokenSigningSignature>
}

declare global {
  interface Window {
    TokenSigning?: new () => TokenSigningInstance
  }
}

export class TokenSigningUnavailableError extends Error {
  constructor() {
    super('tokensigning_not_installed')
    this.name = 'TokenSigningUnavailableError'
  }
}

const TOKENSIGNING_UNAVAILABLE_MESSAGES = new Set([
  'no_implementation',
  'no_certificates',
  'not_allowed',
])

export function isTokenSigningUnavailableError(err: unknown): boolean {
  if (err instanceof TokenSigningUnavailableError) return true
  if (err instanceof Error) return TOKENSIGNING_UNAVAILABLE_MESSAGES.has(err.message)
  return false
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

async function sha256(base64Data: string): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', base64ToBytes(base64Data)))
}

function bytesToHex(bytes: Uint8Array): string {
  let hex = ''
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i].toString(16)
    hex += b.length === 1 ? `0${b}` : b
  }
  return hex
}

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.length % 2 === 0 ? hex : `0${hex}`
  const bytes = new Uint8Array(clean.length / 2)
  for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(clean.substr(i * 2, 2), 16)
  return bytes
}

function hexToBase64(hex: string): string {
  return bytesToBase64(hexToBytes(hex))
}

export async function openTokenSigningSession(): Promise<SigningSession> {
  const { TokenSigning } = window
  if (typeof TokenSigning !== 'function') {
    throw new TokenSigningUnavailableError()
  }

  const tokenSigning = new TokenSigning()
  const cert = await tokenSigning.getCertificate({ lang: 'en' })

  return {
    method: 'tokensigning',
    certificate: hexToBase64(cert.hex),
    sign: async (dataToSign: string) => {
      const hashHex = bytesToHex(await sha256(dataToSign))
      const signature = await tokenSigning.sign(
        cert,
        { hex: hashHex, type: 'SHA-256' },
        { lang: 'en', info: '' },
      )
      return { signature: hexToBase64(signature.hex) }
    },
  }
}

async function isLocalSignerAvailable(baseUrl: string): Promise<boolean> {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
    const response = await fetch(`${baseUrl}/version`, {
      method: 'GET',
      signal: controller.signal,
    })
    clearTimeout(timeoutId)
    return response.ok
  } catch {
    return false
  }
}

export async function isStampitAvailable(): Promise<boolean> {
  return isLocalSignerAvailable(STAMPIT_SERVICE_URL)
}

export async function isSlovakSignerAvailable(): Promise<boolean> {
  return isLocalSignerAvailable(SK_SIGNER_URL)
}

export async function openStampitSession(): Promise<SigningSession> {
  return openLocalSignerSession(STAMPIT_SERVICE_URL, 'stampit')
}

export async function openSlovakSignerSession(): Promise<SigningSession> {
  return openLocalSignerSession(SK_SIGNER_URL, 'sk-signer')
}

async function openLocalSignerSession(baseUrl: string, method: SigningMethod): Promise<SigningSession> {
  const selectRes = await fetch(`${baseUrl}/signer/selectSigner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  if (!selectRes.ok) {
    throw new Error(`${method} selectSigner failed (${selectRes.status})`)
  }
  const selectData = await selectRes.json()
  if (selectData.status !== 'ok') {
    throw new Error(selectData.reasonText || `${method} selectSigner failed`)
  }

  const { sid, signerCert } = selectData

  return {
    method,
    certificate: signerCert,
    sign: async (dataToSign: string) => {
      const signRes = await fetch(`${baseUrl}/signer/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: dataToSign,
          signatureType: 'raw',
          sid,
        }),
      })
      if (!signRes.ok) {
        throw new Error(`${method} sign failed (${signRes.status})`)
      }
      const signData = await signRes.json()
      if (signData.status !== 'ok') {
        throw new Error(signData.reasonText || `${method} sign failed`)
      }
      return { signature: signData.signature }
    },
  }
}

function describeSignatureAlgorithm(algorithm: unknown): string | undefined {
  if (!algorithm || typeof algorithm !== 'object') return undefined

  const { cryptoAlgorithm, hashFunction, paddingScheme } = algorithm as Record<string, string>

  return [cryptoAlgorithm, hashFunction, paddingScheme].filter(Boolean).join('/') || undefined
}

export async function openWebEidSession(): Promise<SigningSession> {
  const certResponse = await webeid.getSigningCertificate({ lang: 'en' })
  const { certificate, supportedSignatureAlgorithms } = certResponse

  // The only place a card says what it can actually do. An ECC card that will not take SHA-256 fails
  // with the same opaque "operation failed" as an unsupported card, and this is what tells them apart.
  // eslint-disable-next-line no-console
  console.info('[signing] web-eid card supports', supportedSignatureAlgorithms)

  return {
    method: 'webeid',
    certificate,
    sign: async (dataToSign: string) => {
      // The second argument is the hash, not the data. The other two providers hash - TokenSigning
      // here, StampIT inside its own service - and this one was handing over the whole SignedInfo
      // where a 32 byte digest was expected.
      const hash = bytesToBase64(await sha256(dataToSign))
      const signResponse = await webeid.sign(certificate, hash, 'SHA-256', { lang: 'en' })

      return {
        signature: signResponse.signature,
        signatureAlgorithm: describeSignatureAlgorithm(signResponse.signatureAlgorithm),
      }
    },
  }
}

/**
 * Whether the card can be stood in for by a key in this file. Off unless a build explicitly asks for
 * it, so a production bundle has no path that reaches {@link openSoftwareSession}.
 */
export function isSoftwareSigningEnabled(): boolean {
  return process.env.REACT_APP_SOFTWARE_SIGNING === 'true'
}

/**
 * Signs with {@link MOCK_PRIVATE_KEY} instead of a card, so the whole chain - prepare, sign, complete,
 * ASiC, MessageContainer, CEP - can be exercised without a stick, a driver or a browser extension.
 *
 * The one thing that differs from the card providers is where the hashing happens. They take a digest,
 * because that is all a card will accept over the wire, so each of them hashes first. WebCrypto takes
 * the data and hashes it itself as part of RSASSA-PKCS1-v1_5, so hashing here would sign the digest of
 * a digest and produce a signature that verifies against nothing.
 */
export async function openSoftwareSession(): Promise<SigningSession> {
  const key = await crypto.subtle.importKey(
    'pkcs8',
    base64ToBytes(MOCK_PRIVATE_KEY),
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['sign'],
  )

  // eslint-disable-next-line no-console
  console.warn('[signing] signing with the built-in test key, not a card')

  return {
    method: 'software',
    certificate: MOCK_CERTIFICATE,
    sign: async (dataToSign: string) => {
      const signature = await crypto.subtle.sign('RSASSA-PKCS1-v1_5', key, base64ToBytes(dataToSign))

      return { signature: bytesToBase64(new Uint8Array(signature)) }
    },
  }
}
